// Kids lead form — POST /api/_platform/kidsLead (public, pre-auth).
// The worker route is an email-only relay: validate → confirmation email
// to the parent via Resend. No persistence — the real kids backend will
// be a separate service; slot availability is a frontend dummy until then
// (see docs/flows/KIDS_LEAD.md).
import { z } from 'zod';

export const KidsGenderSchema = z.enum(['male', 'female', 'other']);
export type KidsGender = z.infer<typeof KidsGenderSchema>;

export const KidsEthnicitySchema = z.enum([
  'European',
  'Hispanic or Latino',
  'African-American',
  'Indigenous',
  'East Asian',
  'South Asian',
  'Southeast Asian',
  'Middle Eastern',
  'Pacific Islander',
  'Multiracial',
  'Other',
  'Not specified',
]);
export type KidsEthnicity = z.infer<typeof KidsEthnicitySchema>;

export const KidsOfficeIdSchema = z.enum(['atl_perimeter', 'duluth_hq']);
export type KidsOfficeId = z.infer<typeof KidsOfficeIdSchema>;

// Meeting date/time are office-local (America/New_York). Slots sit on a
// 10-minute grid, so minutes always end in 0.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]0$/;
const PHONE_RE = /^\+?[\d\s().-]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const KidsChildSchema = z.object({
  name: z.string().trim().min(1).max(100),
  age: z.number().int().min(5).max(17),
  gender: KidsGenderSchema,
  ethnicity: KidsEthnicitySchema,
});
export type KidsChild = z.infer<typeof KidsChildSchema>;

export const KidsLeadReqSchema = z.object({
  parent: z.object({
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().regex(PHONE_RE).max(30),
    email: z.string().trim().regex(EMAIL_RE).max(200),
  }),
  children: z.array(KidsChildSchema).min(1).max(10),
  meeting: z.object({
    office: KidsOfficeIdSchema,
    date: z.string().regex(DATE_RE),
    time: z.string().regex(TIME_RE),
  }),
  // Both consents are required to submit — literal true, not boolean.
  consents: z.object({
    icf: z.literal(true),
    disclaimer: z.literal(true),
  }),
  // Optional until the Turnstile widget is wired into the public form. The
  // worker verifies it only when present (verifyTurnstile fails open with no
  // secret); once the widget ships, tighten back to required.
  turnstileToken: z.string().min(1).max(4096).optional(),
  // Honeypot: hidden field, must stay in the schema so bots that fill it
  // get the silent fake success instead of a distinguishable 400.
  website: z.string().max(200).optional(),
});
export type KidsLeadReq = z.infer<typeof KidsLeadReqSchema>;

export const KidsLeadResSchema = z.object({
  ok: z.literal(true),
  // Receipt only — nothing is stored behind it (no persistence yet).
  leadId: z.string(),
  emailSent: z.boolean(),
});
export type KidsLeadRes = z.infer<typeof KidsLeadResSchema>;
